import React, { useEffect, useRef, useState, useCallback } from "react";
import { Eraser } from "lucide-react";

/**
 * Touch + mouse signature pad. Renders to an internal <canvas> and exposes the
 * current drawing as a PNG data URL via `onChange(dataUrl)`. Parent decides
 * when to read the value (e.g., on submit).
 */
const SignaturePad = ({ value, onChange, width = 480, height = 160, "data-testid": testId = "signature-pad" }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const last = useRef({ x: 0, y: 0 });
  const hasInkRef = useRef(false);
  const lastExportedRef = useRef("");
  const [hasInk, setHasInk] = useState(false);

  const resetCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.lineWidth = 2.4;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.strokeStyle = "#000000";
  }, []);

  useEffect(() => {
    if (!value) {
      resetCanvas();
      hasInkRef.current = false;
      setHasInk(false);
      lastExportedRef.current = "";
      return;
    }

    // Parent echoed our export — canvas already shows the signature.
    if (value === lastExportedRef.current) return;

    const canvas = canvasRef.current;
    if (!canvas) return;

    const img = new Image();
    img.onload = () => {
      resetCanvas();
      canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
      hasInkRef.current = true;
      setHasInk(true);
      lastExportedRef.current = value;
    };
    img.src = value;
  }, [value, resetCanvas]);

  const pointerPos = (e) => {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;
    const pt = e.touches ? e.touches[0] : e;
    return { x: (pt.clientX - rect.left) * scaleX, y: (pt.clientY - rect.top) * scaleY };
  };

  const exportPng = () => {
    const canvas = canvasRef.current;
    if (!canvas) return "";
    return canvas.toDataURL("image/png");
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    last.current = pointerPos(e);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = pointerPos(e);
    ctx.beginPath();
    ctx.moveTo(last.current.x, last.current.y);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    last.current = p;
    if (!hasInkRef.current) {
      hasInkRef.current = true;
      setHasInk(true);
    }
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    if (hasInkRef.current) {
      const png = exportPng();
      lastExportedRef.current = png;
      onChange?.(png);
    }
  };

  const clear = () => {
    resetCanvas();
    hasInkRef.current = false;
    setHasInk(false);
    lastExportedRef.current = "";
    onChange?.("");
  };

  return (
    <div className="space-y-2" data-testid={testId}>
      <div className="relative bg-white border border-white/15 rounded-md overflow-hidden">
        <canvas
          ref={canvasRef}
          width={width}
          height={height}
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
          onTouchCancel={end}
          className="block w-full h-40 cursor-crosshair touch-none"
          style={{ aspectRatio: `${width} / ${height}` }}
          data-testid={`${testId}-canvas`}
        />
        {!hasInk ? (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center text-xs text-gray-500 font-exo bg-white/80">
            Draw your signature using your finger or mouse
          </div>
        ) : null}
      </div>
      <button
        type="button"
        onClick={clear}
        disabled={!hasInk}
        className="inline-flex items-center gap-1.5 text-xs text-spike-text-muted hover:text-spike-text disabled:opacity-40 disabled:cursor-not-allowed font-exo"
        data-testid={`${testId}-clear-btn`}
      >
        <Eraser className="w-3 h-3" />
        Clear
      </button>
    </div>
  );
};

export default SignaturePad;
